import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Checkbox,
  TextField,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  Chip,
  Paper,
  Slide,
  Fab,
} from '@mui/material';
import {
  Import,
  FolderOpen,
  FileText,
  ChevronRight,
  CheckCircle,
  ArrowLeft as ArrowBack,
  ShoppingCart,
  Clock,
  Copy,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { LoadingButton } from '@mui/lab';
import { get_set_history_notes, import_set_history_notes } from 'src/auth/context/jwt/library';

// Search/Filter component
function SearchBar({ onSearch }) {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  return (
    <TextField
      fullWidth
      placeholder="Search sets..."
      value={searchTerm}
      onChange={handleSearch}
      variant="outlined"
      size="small"
      sx={{
        mb: 3,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          '&:hover fieldset': {
            borderColor: '#667eea',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#667eea',
          },
        },
      }}
    />
  );
}

// Exercise Card Component
function ExerciseCard({ exercise, isSelected, onToggle }) {
  // Parse path to get breadcrumb info
  const pathParts = exercise.path.split(' > ');
  const weekName = pathParts[0];
  const dayName = pathParts[1];

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: isSelected ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.08)',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateX(4px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: '#667eea',
        },
      }}
      onClick={() => onToggle(String(exercise.original_id))} // Convert to string
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggle(String(exercise.original_id)); // Convert to string
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: '#667eea',
              '&.Mui-checked': {
                color: '#667eea',
              },
            }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <FileText size={16} color="#667eea" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {exercise.name}
              </Typography>
            </Stack>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
              >
                {exercise?.path}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 500 }}>
                {exercise.notes_count} {exercise.notes_count === 1 ? 'note' : 'notes'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {new Date(exercise.created_at).toLocaleDateString()}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Week Section Component
function WeekSection({ week, selectedExercises, onToggleExercise, searchTerm }) {
  const filteredExercises = week.sets.filter(
    (exercise) =>
      exercise?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      exercise?.day_name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  if (filteredExercises.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <FolderOpen size={20} color="#667eea" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Week {week.weekNumber}
        </Typography>
        <Chip
          label={`${filteredExercises.length} sets`}
          size="small"
          sx={{ bgcolor: '#667eea15', color: '#667eea', fontWeight: 500 }}
        />
      </Stack>

      {filteredExercises.map((exercise) => (
        <ExerciseCard
          key={exercise.id}
          exercise={exercise}
          isSelected={selectedExercises.includes(String(exercise.original_id))} // Convert to string for comparison
          onToggle={onToggleExercise}
        />
      ))}
    </Box>
  );
}

// Main Component
export default function SetNotesHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentSet,
    currentProgram,
    activity_id,
    set_id,
    set_main_id,
    exercise: currentExercise,
  } = location.state || {};
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showBottomBar, setShowBottomBar] = useState(true);

  const [weeksData, setWeeksData] = useState([]);

  // Fetch sets data
  const fetchSets = async () => {
    try {
      setLoading(true);
      const res = await get_set_history_notes({ id: activity_id, set_id: set_main_id });
      setWeeksData(res?.data || []);
    } catch (err) {
      toast.error('Failed to fetch sets. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activity_id) {
      fetchSets();
    }
    // eslint-disable-next-line
  }, [activity_id]);

  const handleToggleExercise = (exerciseOriginalId) => {
    setSelectedExercises((prev) =>
      prev.includes(exerciseOriginalId)
        ? prev.filter((id) => id !== exerciseOriginalId)
        : [...prev, exerciseOriginalId]
    );
    setShowBottomBar(true);
  };

  const handleImportNotes = async () => {
    if (selectedExercises.length === 0) {
      toast.error('Please select at least one exercise to copy notes from');
      return;
    }

    setImporting(true);

    try {
      // Prepare the data payload with original_id as strings
      const importedNotesData = {
        selected_set_ids: selectedExercises.toString(), // This contains original_id values as strings
        set_id, // Make sure set_id is defined
      };

      // Call the API
      const res = await import_set_history_notes(importedNotesData);

      // Show success message
      toast.success(
        res?.message || `Successfully copied notes from ${selectedExercises.length} exercise(s)`
      );

      // Navigate back to the set discussion page
      navigate(-1);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to copy notes. Please try again.');
      console.error('Copy error:', error);
    } finally {
      setImporting(false);
    }
  };

  const getTotalNotesCount = () => {
    let total = 0;
    selectedExercises.forEach((originalId) => {
      weeksData.forEach((week) => {
        const exercise = week.sets.find((e) => String(e.original_id) === originalId); // Compare as strings
        if (exercise) total += exercise.notes_count;
      });
    });
    return total;
  };

  const handleSelectAll = () => {
    const allExerciseOriginalIds = weeksData.flatMap(
      (week) => week.sets.map((ex) => String(ex.original_id)) // Convert to string
    );
    if (selectedExercises.length === allExerciseOriginalIds.length) {
      setSelectedExercises([]);
    } else {
      setSelectedExercises(allExerciseOriginalIds);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#667eea' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, pb: { xs: 12, md: 16 } }}>
      {/* Header */}
      <Stack spacing={3}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<ChevronRight size={16} />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBack size={16} />
            Back to Set
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Clock size={16} />
            Notes History
          </Typography>
        </Breadcrumbs>

        {/* Title Section */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea15, #764ba215)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="overline" sx={{ color: '#667eea', fontWeight: 600 }}>
                  Copy Notes To Set
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {currentSet || 'Current Set'}
                </Typography>
                {currentProgram && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    style={{ fontStyle: 'italic', fontWeight: 'bold' }}
                  >
                    Exercise: {currentExercise?.name}
                  </Typography>
                )}
                {currentProgram && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    style={{ fontStyle: 'italic', fontWeight: 'bold' }}
                  >
                    Program: {currentProgram}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Import Options */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Select Sets to Copy Notes From
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose from the sets below. All notes, comments, and media from selected sets will be
            copied to the current set.
          </Typography>

          {/* Search Bar */}
          <SearchBar onSearch={setSearchTerm} />

          {/* Bulk Actions */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': { borderColor: '#5568d3', bgcolor: 'rgba(102, 126, 234, 0.04)' },
              }}
            >
              {weeksData.length > 0 &&
              selectedExercises.length ===
                weeksData.flatMap((w) => w.sets.map((ex) => String(ex.original_id))).length
                ? 'Deselect All'
                : 'Select All'}
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Weeks Sections */}
          {weeksData.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No sets available to copy from.
              </Typography>
            </Box>
          ) : (
            weeksData.map((week) => (
              <WeekSection
                key={week.id}
                week={week}
                selectedExercises={selectedExercises}
                onToggleExercise={handleToggleExercise}
                searchTerm={searchTerm}
              />
            ))
          )}
        </Paper>
      </Stack>

      {/* Floating Action Button for Mobile */}
      {selectedExercises.length > 0 && (
        <Fab
          variant="extended"
          onClick={() => {
            const element = document.getElementById('selection-summary');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
            bgcolor: '#667eea',
            color: 'white',
            '&:hover': { bgcolor: '#5568d3' },
            gap: 1,
            zIndex: 1000,
          }}
        >
          <ShoppingCart size={20} />
          {selectedExercises.length} Selected
        </Fab>
      )}

      {/* Sticky Bottom Selection Summary */}
      <Slide
        direction="up"
        in={selectedExercises.length > 0 && showBottomBar}
        mountOnEnter
        unmountOnExit
      >
        <Paper
          id="selection-summary"
          elevation={10}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            bgcolor: 'background.paper',
            borderTop: '3px solid #667eea',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
            transform: 'translateZ(0)',
            animation: 'slideUp 0.3s ease-out',
            '@keyframes slideUp': {
              from: {
                transform: 'translateY(100%)',
              },
              to: {
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={{ xs: 2, sm: 3 }}
              sx={{ py: { xs: 2, sm: 3 } }}
            >
              {/* Selection Info */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    bgcolor: '#667eea',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircle size={24} color="white" />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {selectedExercises.length} Set{selectedExercises.length !== 1 ? 's' : ''}{' '}
                    Selected
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Notes:
                    </Typography>
                    <Chip
                      label={getTotalNotesCount()}
                      size="small"
                      sx={{
                        bgcolor: '#667eea',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedExercises([]);
                    setShowBottomBar(false);
                    setTimeout(() => setShowBottomBar(true), 100);
                  }}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': { borderColor: '#5568d3', bgcolor: 'rgba(102, 126, 234, 0.04)' },
                    px: 3,
                  }}
                >
                  Clear All
                </Button>
                <LoadingButton
                  loading={importing}
                  onClick={handleImportNotes}
                  variant="contained"
                  startIcon={<Copy size={18} />}
                  sx={{
                    bgcolor: '#667eea',
                    '&:hover': { bgcolor: '#5568d3' },
                    borderRadius: 2,
                    px: 4,
                    minWidth: 160,
                  }}
                >
                  Copy Notes
                </LoadingButton>
              </Stack>
            </Stack>
          </Container>
        </Paper>
      </Slide>
    </Container>
  );
}
